
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Languages, Hand } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { RoleSwitcher } from './RoleSwitcher';
import { CustomerNavigation } from './CustomerNavigation';
import { NamatScaleButton } from '@/components/customer/NamatScaleButton';

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'ar', label: 'ع' }
] as const;

export function DashboardLayout({ children }: { children: React.ReactNode }) {
  const { logout, currentUser, userData, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const selectedLanguage = languages.find(l => l.code === language);

  // Check if user has volunteer role
  const isVolunteer = hasRole('volunteer');

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  // Check if we're on a customer route
  const isCustomerRoute = location.pathname.startsWith('/dashboard/customer');

  return (
    <div className="min-h-screen bg-secondary/30" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex h-20 items-center justify-between">
            <div className="flex items-center py-6">
              <img 
                src="/namatplatformlogo.png" 
                alt="Namat Platform" 
                className="h-auto w-[196px] object-contain"
              />
            </div>

            <div className={cn(
              "flex items-center gap-2",
              language === 'ar' ? 'sm:mr-0 mr-3' : 'sm:ml-0 ml-2'
            )}>
              {/* Volunteer with us button - only show if user is NOT a volunteer */}
              {!isVolunteer && (
                <>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/vol')}
                    className="hidden md:inline-flex h-9 bg-[#004250] hover:bg-[#003340] text-white border-[#004250]"
                  >
                    <Hand className={`h-4 w-4 ${language === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    {t('auth.volunteerWithUs')}
                  </Button>
                  <Button 
                    variant="outline"
                    size="icon"
                    onClick={() => navigate('/vol')}
                    className="md:hidden h-9 w-9 bg-[#004250] hover:bg-[#003340] text-white border-[#004250]"
                  >
                    <Hand className="h-4 w-4" />
                    <span className="sr-only">{t('auth.volunteerWithUs')}</span>
                  </Button>
                </>
              )}
              
              {/* «مقياس نمط» window (#124): opened from the avatar on the beneficiary's own dashboard; no top-bar icon */}
              {location.pathname.startsWith('/dashboard/customer') && <NamatScaleButton />}

              <div className="h-9">
              <RoleSwitcher />
              </div>
              
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="hidden sm:inline-flex h-9"
              >
                {t('navigation.logout')}
              </Button>
              <Button 
                variant="outline" 
                size="icon"
                onClick={handleLogout}
                className="sm:hidden h-9 w-9"
              >
                <LogOut className="h-4 w-4" />
                <span className="sr-only">{t('navigation.logout')}</span>
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={toggleLanguage}
                className="h-9 w-9"
              >
                <Languages className="h-4 w-4" />
                <span className="sr-only">
                  {language === 'en' ? 'Switch to Arabic' : 'التبديل إلى الإنجليزية'}
                </span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Only show navigation for customer routes */}
      {isCustomerRoute && <CustomerNavigation />}

      <main className="max-w-7xl mx-auto px-4 py-6 md:py-8 lg:py-10">
        {children}
      </main>
    </div>
  );
}